import { h } from 'vue';
import type { DataTableColumnDef } from '@alphadoc/alphadoc-table-grid';
import { DateTime } from 'luxon';
import { RouterLink } from 'vue-router';
import {
  renderSectionTableEmptyValue,
  sectionTableChipMinSize,
  sectionTableColumn,
  sectionTableDateColumn,
  sectionTableNumberColumn,
} from '@/modules/core/utils/section-table-column.util.ts';
import { SECTION_TABLE_DEFAULT_COLUMN_WIDTH } from '@/modules/core/consts/section-table-modal.const.ts';
import SectionTableCopyCell from '@/modules/core/components/card/SectionTableCopyCell.vue';
import type { CounterpartyBankDetails } from '@/modules/counterparty/types/counterparty-bank-details.type.ts';
import type { CounterpartyAddress } from '@/modules/counterparty/types/counterparty-address.type.ts';
import type { CounterpartyContact } from '@/modules/counterparty/types/counterparty-contact.type.ts';
import type { CounterpartyComment } from '@/modules/counterparty/types/counterparty-comment.type.ts';
import type { Counterparty } from '@/modules/counterparty/types/counterparty.type.ts';
import type { DocumentLinkRow } from '@/modules/common/types/document-link-row.type.ts';
import { CounterpartyDistinctOptionsUtil } from '@/modules/counterparty/utils/counterparty-distinct-options.util.ts';
import { CounterpartyOptionsUtil } from '@/modules/counterparty/utils/counterparty-options.util.ts';
import { DocumentCellsUtil } from '@/modules/document/utils/document-cells.util.ts';
import UiProgressCircle from '@/modules/ui/components/ui/UiProgressCircle.vue';
import { ElementType } from '@/modules/viewer/enums/element-type.enum.ts';

function copyCell(value: unknown) {
  const text = String(value ?? '').trim();

  if (!text) {
    return renderSectionTableEmptyValue();
  }

  return h(SectionTableCopyCell, { text });
}

function documentNumberLinkCell(value: unknown) {
  if (value == null || value === '') {
    return renderSectionTableEmptyValue();
  }

  const number = String(value);

  return h(
    RouterLink,
    {
      class: '_blue _hover',
      to: `/document/${number}`,
      target: '_blank',
    },
    () => number
  );
}

export const bankDetailsSectionColumns: DataTableColumnDef<CounterpartyBankDetails>[] = [
  sectionTableColumn('bank', (row) => row['Банк'], 200, { distinct: true }),
  sectionTableColumn('bik', (row) => row['БИК'], 154, { cell: copyCell }),
  sectionTableColumn('ks', (row) => row['КС'], 200, { cell: copyCell }),
  sectionTableColumn('rs', (row) => row['РС'], 154, { cell: copyCell }),
  sectionTableColumn('note', (row) => row['Примечание'] ?? '', 154),
];

export const addressesSectionColumns: DataTableColumnDef<CounterpartyAddress>[] = [
  sectionTableColumn('type', (row) => row['Тип'], 154, { distinct: true }),
  sectionTableColumn('address', (row) => row['Адрес'], 300, { cell: copyCell }),
  sectionTableColumn('note', (row) => row['Примечание'] ?? '', 154),
];

export const contactsSectionColumns: DataTableColumnDef<CounterpartyContact>[] = [
  sectionTableColumn('type', (row) => row['Тип'], 154, { distinct: true }),
  sectionTableColumn('contact', (row) => row['Контакт'], 154, { cell: copyCell }),
  sectionTableColumn('fullName', (row) => row['ФИО'] ?? '', 154, {
    distinct: true,
    cell: copyCell,
  }),
  sectionTableColumn('position', (row) => row['Должность'] ?? '', 154, {
    distinct: true,
  }),
  sectionTableDateColumn('birthDay', (row) => row['ДР'], 154, DateTime.DATE_SHORT),
  sectionTableColumn('note', (row) => row['Примечание'] ?? '', 154),
];

export const commentsSectionColumns: DataTableColumnDef<CounterpartyComment>[] = [
  sectionTableDateColumn('dateTime', (row) => row['Дата время'], 154),
  sectionTableColumn('comment', (row) => row['Комментарий'], 300),
  sectionTableColumn('user', (row) => row['Пользователь'], 154, { distinct: true }),
];

export const nearestDocsSectionColumns: DataTableColumnDef<DocumentLinkRow>[] = [
  sectionTableColumn('link', (row) => row['Ссылка'] ?? '', 60, { distinct: true }),
  sectionTableColumn('type', (row) => row['Тип'], 60, { distinct: true }),
  sectionTableColumn('active', (row) => !!row['Активен'], 60, {
    type: ElementType.Boolean,
    cell: (value) => DocumentCellsUtil.active(value as boolean),
  }),
  sectionTableColumn('number', (row) => row['Номер'], 60, {
    type: ElementType.Number,
    cell: documentNumberLinkCell,
  }),
  sectionTableDateColumn('date', (row) => row['Дата'], 60, DateTime.DATE_SHORT),
  sectionTableColumn('controller', (row) => row['Контролер'] ?? '', 60, {
    distinct: true,
  }),
  sectionTableColumn('project', (row) => row['Проект'] ?? '', 60, { distinct: true }),
  sectionTableColumn('content', (row) => row['Содержание'] ?? '', 60),
  sectionTableDateColumn('deadline', (row) => row['Срок'], 60, DateTime.DATE_SHORT),
  sectionTableColumn('execution', (row) => row['Выполнение'] ?? null, 60, {
    type: ElementType.Number,
    chip: true,
    minSize: sectionTableChipMinSize(24),
    cell: (value) => {
      const progress = value as number | null;

      return progress !== null
        ? h(UiProgressCircle, { value: progress, completedTooltip: 'control.progressFull' })
        : '';
    },
  }),
  sectionTableColumn('user', (row) => row['Пользователь'] ?? '', 60, {
    distinct: true,
  }),
  sectionTableDateColumn('created', (row) => row['Создан'], 60, DateTime.DATE_SHORT),
  sectionTableColumn('regNumber', (row) => row['Рег.номер'] ?? '', 60),
  sectionTableDateColumn('registration', (row) => row['Регистрация'], 60, DateTime.DATE_SHORT),
];

export const counterpartiesSelectColumns: DataTableColumnDef<Counterparty>[] = [
  sectionTableNumberColumn('number', (row) => row.number, SECTION_TABLE_DEFAULT_COLUMN_WIDTH),
  sectionTableColumn('name', (row) => row.name, SECTION_TABLE_DEFAULT_COLUMN_WIDTH),
  sectionTableColumn('type', (row) => row.type, SECTION_TABLE_DEFAULT_COLUMN_WIDTH, {
    type: ElementType.Enum,
    enumOptions: CounterpartyOptionsUtil.types,
  }),
  sectionTableColumn('legalForm', (row) => row.legalForm ?? '', SECTION_TABLE_DEFAULT_COLUMN_WIDTH, {
    type: ElementType.Enum,
    enumOptions: CounterpartyOptionsUtil.legalForms,
  }),
  sectionTableColumn('region', (row) => row.region, SECTION_TABLE_DEFAULT_COLUMN_WIDTH, {
    type: ElementType.Enum,
    enumOptions: CounterpartyDistinctOptionsUtil.region,
  }),
  sectionTableColumn('segment', (row) => row.segment, SECTION_TABLE_DEFAULT_COLUMN_WIDTH, {
    type: ElementType.Enum,
    enumOptions: CounterpartyDistinctOptionsUtil.segment,
  }),
  sectionTableColumn('city', (row) => row.city, SECTION_TABLE_DEFAULT_COLUMN_WIDTH, {
    type: ElementType.Enum,
    enumOptions: CounterpartyDistinctOptionsUtil.city,
  }),
  sectionTableColumn('manager', (row) => row.manager, SECTION_TABLE_DEFAULT_COLUMN_WIDTH, {
    type: ElementType.Enum,
    enumOptions: CounterpartyDistinctOptionsUtil.manager,
  }),
  sectionTableColumn('note', (row) => row.note ?? '', SECTION_TABLE_DEFAULT_COLUMN_WIDTH),
];
